import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

const norm = (s) => (s || "").toString().toLowerCase();

export function FgSearch({ options, value, onChange, placeholder = "Type FG code / model / customer…", testId, className = "", required, disabled, allLabel }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  const selected = options.find((o) => String(o.value) === String(value));
  const list = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    const hits = words.length ? options.filter((o) => { const s = norm(o.search || o.label); return words.every((w) => s.includes(w)); }) : options;
    return hits.slice(0, 60);
  }, [options, q]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const pick = (o) => { onChange({ target: { value: o ? o.value : "" } }); setQ(""); setOpen(false); };
  const key = (e) => {
    if (!open && ["ArrowDown", "Enter"].includes(e.key)) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (list[hi]) pick(list[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div ref={box} className={`relative ${className}`} data-testid={testId ? `${testId}-wrap` : undefined}>
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input className="pp-input w-full pl-7 pr-7 text-xs h-9" disabled={disabled} required={required && !value}
          value={open ? q : selected ? selected.label : (value && !selected ? String(value) : "")}
          placeholder={selected ? selected.label : (allLabel || placeholder)}
          onFocus={() => { setOpen(true); setHi(0); }} onChange={(e) => { setQ(e.target.value); setHi(0); if (!open) setOpen(true); }} onKeyDown={key}
          data-testid={testId} autoComplete="off" />
        {value && !disabled && <button type="button" className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" onClick={() => pick(null)} title="Clear" data-testid={testId ? `${testId}-clear` : undefined}><X size={12} /></button>}
      </div>
      {open && !disabled && (
        <ul className="absolute z-50 mt-1 max-h-64 w-full min-w-[280px] overflow-auto rounded-md border border-slate-200 bg-white shadow-lg text-xs" role="listbox" data-testid={testId ? `${testId}-list` : undefined}>
          {allLabel && !q && <li className="cursor-pointer px-2 py-1.5 text-slate-500 hover:bg-slate-100" onMouseDown={(e) => { e.preventDefault(); pick(null); }}>{allLabel}</li>}
          {list.map((o, i) => <li key={o.value} role="option" aria-selected={i === hi} className={`cursor-pointer px-2 py-1.5 ${i === hi ? "bg-blue-50" : "hover:bg-slate-50"} ${String(o.value) === String(value) ? "font-bold text-blue-800" : ""}`} onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); pick(o); }} data-testid={testId ? `${testId}-opt-${o.value}` : undefined}>{o.label}</li>)}
          {!list.length && <li className="px-2 py-2 text-slate-400">No match for “{q}”</li>}
          {list.length === 60 && <li className="px-2 py-1 text-[10px] text-slate-400">Showing first 60 — keep typing to narrow</li>}
        </ul>
      )}
    </div>
  );
}

export const custOptions = (customers, extra) => (customers || []).map((c) => {
  const fg = c.fg_code || c.zt_code || "";
  const label = `${fg || "—"} · ${c.customer}${c.model && c.model !== fg ? ` · ${c.model}` : ""}${c.colour ? ` · ${c.colour}` : ""}${c.sku ? ` · ${c.sku}` : ""}${extra ? extra(c) : ""}`;
  return { value: c.id, label, search: `${fg} ${c.zt_code || ""} ${c.customer} ${c.model || ""} ${c.sku || ""} ${c.colour || ""}` };
});
